import React, { useState, useMemo, useCallback } from "react";

import groupBy from "lodash/groupBy";
import flatten from "lodash/flatten";
import sortBy from "lodash/sortBy";

import {
  Table,
  TableHead,
  TableRow,
  Box,
  TableCell,
  Typography,
  TableBody,
  Button,
  styled,
} from "@material-ui/core";
import { MeterReading, Meter, getRate } from "../../../api/meters";
import { InlineType } from "../../../components/InlineType";
import { ColumnHeading } from "../../../components/Table";
import { formatDate, parseDate } from "../../../utils/formatDate";

// Remove left padding to make it left align with other component
// Also remove hover color
const StyledButton = styled(Button)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
  "&:hover": {
    background: "transparent !important",
  },
}));

const BottomlessTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
}));

export const MeterReadingsList: React.FC<{
  readings: MeterReading[];
  meter: Meter;
}> = ({ readings, meter }) => {
  const [displayRow, setDisplayRow] = useState<number>(1);

  const getName = useCallback(
    (registerId: string) => {
      const register = meter.registers.find((r) => r.id === registerId);
      return register ? getRate(register) : registerId;
    },
    [meter.registers],
  );

  const formatReading = useCallback(
    (reading: string | null, unit: string) =>
      reading == null ? "" : reading + " " + unit,
    [],
  );

  // group and sort readings by date
  // return only the first group if view less
  const { readingGroups, hasMore } = useMemo(() => {
    const groups = groupBy(readings, (reading) =>
      formatDate(reading.readingDttm),
    );
    const rg = Object.keys(groups)
      .sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime())
      .slice(0, displayRow)
      .map((g) => ({
        date: g,
        readings: sortBy(groups[g], (r) => getName(r.registerId)),
      }));
    return {
      readingGroups: rg,
      hasMore: readings.length > rg.reduce((p, g) => p + g.readings.length, 0),
    };
  }, [readings, getName, displayRow]);

  const { columns } = useMemo(
    () => ({
      columns: [
        {
          header: "Date",
          accessor: "readingDttm", // shouldn't be called
        },
        {
          header: "Sequence Type",
          accessor: "sequenceType",
        },
        {
          header: "Rate",
          accessor: (row: MeterReading) => getName(row.registerId),
        },
        {
          header: "Reading",
          align: "right",
          accessor: (row: MeterReading) =>
            formatReading(row.cumulative, row.unit),
          fontWeight: 700,
        },
        {
          header: "Consumption",
          align: "right",
          paddingRight: 5,
          accessor: (row: MeterReading) =>
            formatReading(row.consumption, row.unit),
        },
        {
          header: "Source",
          accessor: "source",
        },
        {
          header: "Quality",
          accessor: "quality",
        },
      ],
    }),
    [getName, formatReading],
  );

  const rows = useMemo(() => {
    const makeCell = (
      content: string,
      fontWeight: number,
      paddingRight?: number,
    ) => (
      <Box my={1} pr={paddingRight || 0}>
        <Typography variant="body1">
          <InlineType fontWeight={fontWeight}>{content}</InlineType>
        </Typography>
      </Box>
    );

    const tableRows = readingGroups.map((group) =>
      group.readings.map((reading, rowIndex) => (
        <TableRow key={reading.id} data-testid="MeterReadingList__Row">
          {columns.map((column, colIndex) => {
            const { accessor, align, fontWeight, paddingRight } = column;
            let content;
            if (colIndex === 0) {
              content = rowIndex === 0 ? group.date : "";
            } else if (typeof accessor === "string") {
              content = String((reading as any)[String(accessor)]);
            } else if (typeof accessor === "function") {
              content = String(accessor.call(null, reading));
            } else {
              throw new Error("Unexpected accessor");
            }
            const appliedFontWeight = fontWeight || 500;
            return colIndex === 0 && rowIndex < group.readings.length - 1 ? (
              <BottomlessTableCell
                align={align as any}
                padding="none"
                key={reading.id + "_" + column.header}
              >
                {makeCell(content, appliedFontWeight, paddingRight)}
              </BottomlessTableCell>
            ) : (
              <TableCell
                align={align as any}
                padding="none"
                key={reading.id + "_" + column.header}
              >
                {makeCell(content, appliedFontWeight, paddingRight)}
              </TableCell>
            );
          })}
        </TableRow>
      )),
    );

    return <>{flatten(tableRows)}</>;
  }, [columns, readingGroups]);

  return readings.length === 0 ? null : (
    <>
      <Table data-testid="MeterReadingList__Table">
        <TableHead>
          <TableRow>
            {columns.map(({ align, header, paddingRight }) => (
              <TableCell align={align as any} padding="none" key={header}>
                <Box mb={1} pr={paddingRight || 0}>
                  <ColumnHeading>{header}</ColumnHeading>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
      {hasMore === true || (hasMore === false && displayRow > 1) ? (
        <StyledButton
          size="large"
          onClick={() => setDisplayRow(displayRow === 1 ? readings.length : 1)}
          type="button"
          variant="text"
          color="primary"
          data-testid="MeterReadingList__Toggle"
        >
          {hasMore ? "View more" : "View less"}
        </StyledButton>
      ) : null}
    </>
  );
};
